import { addDays, parseYmd, sprintKeyOf, sprintStart, startOfWeek, toYmd } from './rules.js';
/**
 * Повторяющиеся задачи (3.19) чистыми функциями.
 *
 * Серия — это цепочка обычных задач, по экземпляру на период. Копии заводит
 * сервер (`sync/repeat.ts`), но где окажется следующая и куда уедет её срок,
 * знают и карточка в интерфейсе («следующая — в неделе 44»), и агент, поэтому
 * правило одно и живёт здесь.
 */
/** Сколько раз сдвиг ищет текущий период, прежде чем сдаться: сто лет вперёд. */
const MAX_STEPS = 1200;
/**
 * Период, с которым серия повторяется на деле: не чаще спринта (3.19.2).
 * Недельный повтор в месячном режиме — одна копия в месяц, а не пять
 * одинаковых задач в одном спринте.
 */
export function repeatPeriod(repeat, sprintType) {
    return repeat === 'month' || sprintType === 'month' ? 'month' : 'week';
}
/**
 * Серия, к которой принадлежит задача. Повтор, поставленный без серии (агентом
 * или до того, как устройство узнало про поле), начинает серию с самой задачи.
 */
export function seriesOf(task) {
    if (task.series_id != null)
        return task.series_id;
    return task.repeat != null ? task.id : null;
}
/**
 * Переносит ли автоперенос незакрытую задачу в новый спринт (3.11.6, 3.19.3).
 *
 * Не переносятся ни голова серии, ни её прошлые экземпляры: у новой недели
 * свой экземпляр, а прошлый остаётся историей. Выключение повтора снимает и
 * серию (`setRepeat` в интерфейсе, правка через API), поэтому бывшая голова
 * снова переносится как обычная задача.
 *
 * Именно `== null`: у записей, заведённых до повторов, поля может не быть
 * вовсе (см. `canBeProject`).
 */
export function rollsOver(task) {
    return task.repeat == null && task.series_id == null;
}
/**
 * Поля правки «повтор»: включение начинает серию с самой задачи, если её ещё
 * не было, выключение снимает и серию — бывшая голова снова переносится как
 * обычная задача (3.19.6). Пустой объект — менять нечего, и мутацию не шлют.
 * Одна функция на интерфейс и API: что меняется вместе с чем, решается здесь.
 */
export function repeatChange(task, repeat) {
    if (repeat === (task.repeat ?? null))
        return {};
    if (repeat === null)
        return { repeat: null, series_id: null };
    return { repeat, series_id: task.series_id ?? task.id };
}
/** Дата через `months` месяцев; 31-е в коротком месяце — его последний день. */
export function addMonthsClamped(date, months) {
    const first = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), last));
}
/**
 * Месяц, которому принадлежит спринт. У недели — месяц её четверга, как у
 * ISO-недель: неделя с 29 сентября по 5 октября — октябрьская. Без этого
 * месячная копия без срока, заведённая в «первую неделю октября», считала бы
 * себя сентябрьской и следующей поставила бы себя же.
 */
function monthOf(sprint) {
    const start = sprintStart(sprint);
    const anchor = sprint.startsWith('w:') ? addDays(start, 3) : start;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
}
/** Неделя, четверг которой первый в месяце, — «первая неделя месяца» (3.19.4). */
function firstWeekOf(month) {
    const shift = (4 - month.getDay() + 7) % 7;
    return startOfWeek(addDays(month, shift));
}
/** Экземпляр через `steps` периодов от головы. */
export function occurrenceAt(head, steps, sprintType) {
    const period = repeatPeriod(head.repeat, sprintType);
    const due = head.due === null ? null : parseYmd(head.due);
    if (period === 'week') {
        const base = startOfWeek(sprintStart(head.sprint));
        return {
            sprint: sprintKeyOf(addDays(base, 7 * steps), 'week'),
            due: due === null ? null : toYmd(addDays(due, 7 * steps)),
        };
    }
    const nextDue = due === null ? null : addMonthsClamped(due, steps);
    const month = addMonthsClamped(monthOf(head.sprint), steps);
    if (sprintType === 'month') {
        return { sprint: sprintKeyOf(month, 'month'), due: nextDue === null ? null : toYmd(nextDue) };
    }
    // Месячный повтор в недельном режиме: копия появляется в той неделе, куда
    // попадает её срок, а без срока — в первой неделе месяца (3.19.4).
    const week = nextDue ?? firstWeekOf(month);
    return { sprint: sprintKeyOf(week, 'week'), due: nextDue === null ? null : toYmd(nextDue) };
}
/** Где окажется следующий экземпляр — для подписи в карточке. */
export function nextOccurrence(head, sprintType) {
    return occurrenceAt(head, 1, sprintType);
}
/**
 * Экземпляр, который пора завести сегодня, или `null`, если рано.
 *
 * Пропущенные периоды не заводятся задним числом: если повтор включили у
 * задачи трёхнедельной давности или сервер лежал, копия одна — в текущем
 * периоде, со сроком, сдвинутым на столько же периодов. Три копии в прошлых
 * неделях были бы ровно тем накоплением, от которого повтор и спасает.
 */
export function dueOccurrence(head, today, sprintType) {
    const now = today.getTime();
    let current = occurrenceAt(head, 1, sprintType);
    if (sprintStart(current.sprint).getTime() > now)
        return null;
    for (let steps = 2; steps <= MAX_STEPS; steps += 1) {
        const next = occurrenceAt(head, steps, sprintType);
        if (sprintStart(next.sprint).getTime() > now)
            break;
        current = next;
    }
    return current;
}
//# sourceMappingURL=repeat.js.map